import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Recycle, Loader2, X, Pencil, Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { SALVAGE_CONDITIONS, VALUE_TYPES_SIMPLE } from "./specializedConstants";
import { formatCurrency } from "./inventoryConstants";
import { ConfirmDialog } from "./ConfirmDialog";
import { useT } from "@/i18n/LanguageContext";

const empty = { source_unit_id: "", part_number: "", description: "", quantity: 1, condition: "Good", value: 0, value_type: "estimated", location_id: "", notes: "", photo: null, add_to_inventory: false };

const SalvageModal = ({ open, onClose, item, vehicles, locations, currentUser, onSaved }) => {
  const [form, setForm] = useState(empty);
  const isEdit = !!item?.id;
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";
  useEffect(() => { if (open) setForm(item ? { ...empty, ...item } : empty); }, [open, item]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handlePhoto = (e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 10 * 1024 * 1024) { toast.error("Under 10MB"); return; } const r = new FileReader(); r.onload = (ev) => set("photo", ev.target.result); r.readAsDataURL(f); };

  const save = async () => {
    if (!form.description?.trim()) { toast.error("Description required"); return; }
    const unit = vehicles.find((v) => v.id === form.source_unit_id);
    const payload = { ...form, quantity: Number(form.quantity) || 1, value: Number(form.value) || 0, source_unit_number: unit?.unit_number, source_unit_type: unit?.vehicle_type, location_id: form.location_id || null };
    try {
      if (isEdit) { await inventoryApi.updateSalvagedPart(item.id, { ...payload, updated_by: userName }); toast.success("Updated"); }
      else { await inventoryApi.createSalvagedPart({ ...payload, created_by: userName }); toast.success("Salvaged part added"); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="salvage-modal">
        <DialogTitle className="sr-only">{isEdit ? "Edit" : "Add"} Salvaged Part</DialogTitle>
        <DialogDescription className="sr-only">Salvaged part record</DialogDescription>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? "Edit" : "Add"} Salvaged Part</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div><Label className="text-xs text-slate-500">Description *</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="salvage-desc-input" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Part Number</Label><Input value={form.part_number || ""} onChange={(e) => set("part_number", e.target.value)} /></div>
            <div><Label className="text-xs text-slate-500">Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></div>
          </div>
          <div><Label className="text-xs text-slate-500">Source Unit (dismantled)</Label>
            <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.source_unit_id || ""} onChange={(e) => set("source_unit_id", e.target.value)}>
              <option value="">None</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>Unit {v.unit_number}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Condition</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.condition || ""} onChange={(e) => set("condition", e.target.value)}>
                {SALVAGE_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-slate-500">Location</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.location_id || ""} onChange={(e) => set("location_id", e.target.value)}>
                <option value="">Unassigned</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.label || l.container}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Value ($)</Label><Input type="number" min="0" step="0.01" value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="salvage-value-input" /></div>
            <div><Label className="text-xs text-slate-500">Value Type</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.value_type} onChange={(e) => set("value_type", e.target.value)}>
                {VALUE_TYPES_SIMPLE.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          <div className="flex items-center gap-3">
            {form.photo ? <div className="relative"><img src={form.photo} alt="" className="w-14 h-14 rounded object-cover border" /><button onClick={() => set("photo", null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button></div>
              : <label className="flex items-center gap-1 text-sm text-blue-600 cursor-pointer"><Upload className="w-4 h-4" /> Photo<input type="file" accept="image/*" className="hidden" onChange={handlePhoto} /></label>}
          </div>
          {!isEdit && (
            <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 p-2 rounded-lg" data-testid="salvage-add-inv">
              <input type="checkbox" checked={form.add_to_inventory} onChange={(e) => set("add_to_inventory", e.target.checked)} /> Also add to main Parts Inventory (status: Salvaged)
            </label>
          )}
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-slate-200 flex-shrink-0">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" onClick={save} data-testid="salvage-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SalvagedPartsSection = ({ currentUser, canManage, vehicles }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const { t } = useT();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try { setItems(await inventoryApi.getSalvagedParts()); } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);
  useEffect(() => { inventoryApi.getLocations().then(setLocations).catch(() => {}); }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const doDelete = async () => {
    try { await inventoryApi.deleteSalvagedPart(delItem.id); toast.success(t("Deleted")); setDelItem(null); fetchItems(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const totalValue = items.reduce((s, i) => s + (i.value || 0), 0);

  return (
    <div className="space-y-4" data-testid="salvaged-parts-section">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{items.length} salvaged parts · <span className="font-semibold text-slate-700">{formatCurrency(totalValue)}</span> value</div>
        {canManage && <Button onClick={() => { setEdit(null); setShow(true); }} data-testid="add-salvage-btn"><Plus className="w-4 h-4 mr-1" /> Add Salvaged Part</Button>}
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : items.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><Recycle className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No salvaged parts</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((i) => (
            <div key={i.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3" data-testid={`salvage-card-${i.id}`}>
              {i.photo ? <img src={i.photo} alt="" className="w-12 h-12 rounded-lg object-cover border" /> : <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center"><Recycle className="w-6 h-6 text-purple-400" /></div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between"><span className="font-semibold text-slate-900 truncate">{i.description}</span>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => { setEdit(i); setShow(true); }} className="p-1 text-slate-400 hover:text-blue-600" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setDelItem(i)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`delete-salvage-${i.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}</div>
                <div className="text-xs text-slate-500">{i.part_number ? `${i.part_number} · ` : ""}Qty {i.quantity} · {i.condition}</div>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <span className="font-semibold text-slate-900">{formatCurrency(i.value)}</span>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{i.value_type}</span>
                  {i.source_unit_number && <span className="text-slate-400">from Unit {i.source_unit_number}</span>}
                  {i.linked_part_id && <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-600">In Inventory</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <SalvageModal open={show} onClose={() => setShow(false)} item={edit} vehicles={vehicles} locations={locations} currentUser={currentUser} onSaved={fetchItems} />
      <ConfirmDialog open={!!delItem} onClose={() => setDelItem(null)} onConfirm={doDelete} title={delItem ? `${t("Delete")} ${delItem.description}?` : ""} message={t("This removes it from active lists. The record is kept in the audit trail.")} />
    </div>
  );
};
